import React, { useEffect, useState } from 'react'

import { graphql } from '../../../../services/graphql'

import { buildComponent } from '../../../../components/factory'

import * as UI from '@mantine/core'
import * as Icons from '../../../../components/icons'

type ClientAccount = {
  _id: string
  email: string
  active: boolean
  spaces: string[]
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const errorLabel = (code: string) => {
  switch (code) {
    case 'email_required':
      return 'Veuillez saisir un email.'
    // Un compte d'accès existant est désormais rattaché automatiquement : ce
    // refus ne concerne plus que les adresses d'un compte administrateur.
    case 'email_already_used':
      return 'Cette adresse est celle d’un compte administrateur : elle ne peut pas servir de compte d’accès.'
    default:
      return 'Une erreur est survenue.'
  }
}

export const SectionAccounts = buildComponent<{
  space: string
  emails?: { email: string }[]
}>()
  .withLifecycle(({ props }) => {
    const [accounts, setAccounts] = useState<ClientAccount[]>([])
    const [loading, setLoading] = useState(true)
    const [email, setEmail] = useState('')
    const [creating, setCreating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Dernier code généré à afficher (création ou régénération).
    const [generated, setGenerated] = useState<{
      email: string
      code: string
    } | null>(null)
    // Email d'un compte existant qu'on vient de rattacher à cet espace.
    const [attached, setAttached] = useState<string | null>(null)

    const reload = async () => {
      setLoading(true)
      const { administratorClientAccounts } =
        await graphql.queries.getClientAccounts(props.space)
      setAccounts(administratorClientAccounts ?? [])
      setLoading(false)
    }

    useEffect(() => {
      reload().catch(() => setLoading(false))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.space])

    const createAccount = async () => {
      const value = email.toLowerCase().trim()
      setError(null)
      setAttached(null)

      if (!isValidEmail(value)) {
        setError('Veuillez saisir un email valide.')
        return
      }

      setCreating(true)
      try {
        const { administratorCreateClientAccount: result } =
          await graphql.mutations.createClientAccount(value, props.space)

        if (result.error) {
          setError(errorLabel(result.error))
          return
        }

        // Compte existant rattaché à cet espace en plus : il conserve son code,
        // il n'y a donc rien de nouveau à communiquer.
        if (result.attached) {
          setAttached(result.email as string)
        } else {
          setGenerated({
            email: result.email as string,
            code: result.code as string,
          })
        }

        setEmail('')
        await reload()
      } finally {
        setCreating(false)
      }
    }

    const regenerate = async (id: string) => {
      const { administratorRegenerateClientCode: result } =
        await graphql.mutations.regenerateClientCode(id)

      if (!result.error) {
        setGenerated({
          email: result.email as string,
          code: result.code as string,
        })
      }
    }

    const remove = async (id: string) => {
      // On transmet l'espace courant : si le compte est rattaché à d'autres
      // espaces, l'API se contente de l'en détacher.
      await graphql.mutations.deleteClientAccount(id, props.space)
      setAccounts((list) => list.filter((account) => account._id !== id))
    }

    const copyCode = (code: string) => {
      navigator.clipboard?.writeText(code).catch(() => {})
    }

    // Adresses déjà destinataires des récapitulatifs (mensuels/annuels),
    // proposées en autocomplétion pour éviter de les ressaisir. On exclut
    // celles qui ont déjà un compte, et on dédoublonne (insensible à la casse).
    const usedEmails = new Set(
      accounts.map((account) => account.email.toLowerCase()),
    )
    const seen = new Set<string>()
    const recapSuggestions = (props.emails ?? [])
      .map((entry) => entry.email)
      .filter((value) => {
        const key = value?.toLowerCase()
        if (!key || usedEmails.has(key) || seen.has(key)) {
          return false
        }
        seen.add(key)
        return true
      })

    return {
      accounts,
      loading,
      email,
      setEmail,
      creating,
      error,
      generated,
      setGenerated,
      attached,
      setAttached,
      createAccount,
      regenerate,
      remove,
      copyCode,
      recapSuggestions,
    }
  })
  .withRender(({ lifecycle }) => (
    <UI.Input.Wrapper
      sx={{ position: 'relative' }}
      label={<b style={{ fontSize: '2rem' }}>Comptes d'accès</b>}
      description={
        <UI.Text mt={8} size="sm" sx={{ maxWidth: '124rem' }}>
          Qui peut se connecter au back-office pour suivre cet espace. Une
          adresse inconnue crée un compte et son code ; une adresse qui a déjà
          un compte se voit simplement ajouter cet espace.
        </UI.Text>
      }
    >
      {lifecycle.generated && (
        <UI.Alert
          mt={16}
          color="green"
          title="Code généré"
          withCloseButton
          onClose={() => lifecycle.setGenerated(null)}
        >
          <UI.Group spacing={8}>
            <UI.Text>
              {lifecycle.generated.email} —{' '}
              <UI.Code sx={{ fontSize: '1.6rem' }}>
                {lifecycle.generated.code}
              </UI.Code>
            </UI.Text>
            <UI.Button
              size="xs"
              variant="light"
              onClick={() => lifecycle.copyCode(lifecycle.generated!.code)}
            >
              Copier
            </UI.Button>
          </UI.Group>
          <UI.Text size="xs" color="dimmed" mt={8}>
            Affiché une seule fois — régénérez-en un en cas de perte.
          </UI.Text>
        </UI.Alert>
      )}

      {lifecycle.attached && (
        <UI.Alert
          mt={16}
          color="blue"
          title="Accès ajouté à un compte existant"
          withCloseButton
          onClose={() => lifecycle.setAttached(null)}
        >
          <UI.Text>
            <b>{lifecycle.attached}</b> avait déjà un compte : cet espace y a
            été ajouté.
          </UI.Text>
          <UI.Text size="xs" color="dimmed" mt={8}>
            Son code reste inchangé — rien de nouveau à lui communiquer.
          </UI.Text>
        </UI.Alert>
      )}

      <UI.Card p={0} mt={16} withBorder>
        {lifecycle.accounts.map((account) => (
          <React.Fragment key={account._id}>
            <UI.Group px={16} py={12} position="apart" noWrap>
              <div style={{ minWidth: 0 }}>
                <UI.Text
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {account.email}
                </UI.Text>
                {/* Un compte peut couvrir plusieurs espaces : le dire ici évite
                    de croire que la corbeille supprime le compte entier. */}
                {account.spaces.length > 1 && (
                  <UI.Text size="xs" color="dimmed">
                    Également sur {account.spaces.length - 1} autre
                    {account.spaces.length > 2 ? 's' : ''} espace
                    {account.spaces.length > 2 ? 's' : ''}
                  </UI.Text>
                )}
              </div>
              <UI.Group spacing={8} noWrap sx={{ flexShrink: 0 }}>
                <UI.Button
                  size="xs"
                  variant="subtle"
                  onClick={() => lifecycle.regenerate(account._id)}
                >
                  Régénérer le code
                </UI.Button>
                <UI.ActionIcon
                  size="lg"
                  title={
                    account.spaces.length > 1
                      ? "Retirer l'accès à cet espace"
                      : 'Supprimer le compte'
                  }
                  onClick={() => lifecycle.remove(account._id)}
                >
                  <UI.Text color="red">
                    <Icons.FontAwesomeIcon
                      color="currentColor"
                      icon={Icons.Solid.faTrash}
                    />
                  </UI.Text>
                </UI.ActionIcon>
              </UI.Group>
            </UI.Group>
            <UI.Divider />
          </React.Fragment>
        ))}

        {!lifecycle.loading && lifecycle.accounts.length === 0 && (
          <UI.Text p={16} color="dimmed" size="sm">
            Aucun compte d'accès pour cet espace.
          </UI.Text>
        )}

        <UI.Group p={16} spacing={8} noWrap sx={{ flex: 1 }}>
          {/* `minWidth: 0` est indispensable : un élément flex a `min-width:
              auto`, donc sans ça le champ refuse de rétrécir sous sa largeur de
              contenu et pousse le bouton hors du conteneur. */}
          <UI.Autocomplete
            placeholder="Email de la personne à autoriser"
            sx={{ flex: 1, minWidth: 0 }}
            // Le thème impose `Autocomplete.wrapper.width = 480` à toute
            // l'application (src/theme/index.ts). Dans une ligne flex, ce
            // wrapper débordait de son conteneur et passait sous le bouton.
            // On neutralise la largeur fixe ici plutôt que dans le thème, dont
            // le rayon d'impact couvre tous les autres écrans.
            styles={{
              wrapper: { width: '100%', minWidth: 0 },
              input: { width: '100%', minWidth: 0 },
            }}
            withinPortal
            limit={20}
            nothingFound={null}
            data={lifecycle.recapSuggestions}
            value={lifecycle.email}
            onChange={lifecycle.setEmail}
            onKeyDown={(event) => {
              // Entrée valide la création, sauf quand une suggestion est
              // surlignée dans la liste : l'Autocomplete la sélectionne alors.
              if (
                event.key === 'Enter' &&
                !event.currentTarget.getAttribute('aria-activedescendant')
              ) {
                lifecycle.createAccount()
              }
            }}
          />
          <UI.Button
            size="sm"
            sx={{ minHeight: 38, flexShrink: 0 }}
            px="2rem"
            loading={lifecycle.creating}
            disabled={!lifecycle.email.trim()}
            onClick={lifecycle.createAccount}
          >
            Donner l'accès
          </UI.Button>
        </UI.Group>
        {lifecycle.error && (
          <UI.Text color="red" size="sm" mx={16} mb={8}>
            {lifecycle.error}
          </UI.Text>
        )}
      </UI.Card>

      {lifecycle.loading && (
        <UI.Loader
          sx={{ position: 'absolute', top: '1rem', right: '1rem' }}
          size="xs"
        />
      )}
    </UI.Input.Wrapper>
  ))
